// Animated Fluent emoji + the starfield backdrop.
import { useState, useMemo } from "react";
import { C } from "../design-system.js";

export function Stars({ count = 5 }) {
  const stars = useMemo(() =>
    Array.from({ length: count }).map(() => ({
      w: Math.random() * 2 + 1, op: Math.random() * 0.4 + 0.1,
      top: Math.random() * 100, left: Math.random() * 100,
      dur: Math.random() * 3 + 2, delay: Math.random() * 3,
    })), []);
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {stars.map((s, i) => (
        <div key={i} style={{
          position: "absolute", width: s.w, height: s.w, borderRadius: "50%",
          background: C.cream, opacity: s.op, top: `${s.top}%`, left: `${s.left}%`,
          animation: `twinkle ${s.dur}s ease-in-out infinite`, animationDelay: `${s.delay}s`,
        }} />
      ))}
    </div>
  );
}

// ── Animated Fluent Emoji ─────────────────────────────────────────────────────
// Loads Microsoft's open-source animated emoji (animated PNG) by unicode codepoint.
// Falls back to the plain emoji character when no animated version exists or load fails.
// Self-hosted (was GitHub raw CDN, blocked in Iran) — PNGs live in frontend/public/emoji
export const FLUENT_BASE = "/emoji";

// Emoji that are NOT animated in the repo → render as plain text, skip the network request
export const NO_ANIM = new Set(["🌸","🍀","🌺","🌷","🎈","🎨","🎸","⚽","🏰","🎠","👑","💎","🎭","🪁","🌙","☀️","❄️","✨","📋","🏆","🎁","❤️","📢","🌱","🚧"]);

export function emojiToCodepoint(emoji) {
  // Strip variation selectors for the filename
  return Array.from(emoji)
    .map(ch => ch.codePointAt(0).toString(16))
    .filter(cp => cp !== "fe0f")
    .join("-");
}

export function FluentEmoji({ children, size = 24, style = {}, anim = "float" }) {
  const emoji = children;
  const [failed, setFailed] = useState(false);
  const hasAnim = !NO_ANIM.has(emoji) && !failed;

  if (hasAnim) {
    const cp = emojiToCodepoint(emoji);
    return (
      <img
        src={`${FLUENT_BASE}/${cp}.png`}
        alt={emoji}
        onError={() => setFailed(true)}
        style={{ width: size, height: size, objectFit: "contain", verticalAlign: "middle", display: "inline-block", ...style }}
      />
    );
  }

  // Fallback: plain emoji with a gentle CSS animation so it still feels alive
  return (
    <span style={{ display: "inline-block", fontSize: size, lineHeight: 1, animation: `${anim} 2.5s ease-in-out infinite`, ...style }}>
      {emoji}
    </span>
  );
}

// Static emoji avatar (used inside tabs/small chips where animation would be noisy)
export function EmojiAvatar({ children, size = 24, animated = true, style = {} }) {
  const emoji = children;
  const [failed, setFailed] = useState(false);
  if (animated && !NO_ANIM.has(emoji) && !failed) {
    const cp = emojiToCodepoint(emoji);
    return (
      <img src={`${FLUENT_BASE}/${cp}.png`} alt={emoji} onError={() => setFailed(true)}
        style={{ width: size, height: size, objectFit: "contain", verticalAlign: "middle", display: "inline-block", ...style }} />
    );
  }
  return <span style={{ fontSize: size, lineHeight: 1, ...style }}>{emoji}</span>;
}

